import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { fetchSeriesMetrics, fetchTopicMetrics, SeriesMetricsData, TopicMetricsData, SeriesIndicator } from "../../api";
import { shortPeriodLabel } from "../../utils/date";

// ── types ─────────────────────────────────────────────────────────────────────

type PeriodMode = "quarterly" | "annual" | "monthly";

interface GroupedIndicators {
  [category: string]: string[];
}

// ── helpers ───────────────────────────────────────────────────────────────────

/** Convert period_label to sortable key */
function periodSortKey(label: string): string {
  if (!label) return "0000-00-00";
  const monthly = label.match(/^(\d{4})-(\d{2})$/);
  if (monthly) return `${monthly[1]}-${monthly[2]}-15`;
  const singleQ = label.match(/^(\d{4})Q(\d)$/);
  if (singleQ) return `${singleQ[1]}-${String(Number(singleQ[2]) * 3).padStart(2, "0")}-15`;
  const annual = label.match(/^(\d{4})Y$/);
  if (annual) return `${annual[1]}-12-25`;
  const half = label.match(/^(\d{4})H(\d)$/);
  if (half) return `${half[1]}-${half[2] === "1" ? "06" : "12"}-15`;
  const cumQ = label.match(/^(\d{4})Q\d-Q(\d)$/);
  if (cumQ) return `${cumQ[1]}-${String(Number(cumQ[2]) * 3).padStart(2, "0")}-20`;
  return label;
}

function isSingleQuarter(label: string) { return /^\d{4}Q\d$/.test(label); }
function isAnnual(label: string)        { return /^\d{4}Y$/.test(label); }
function isMonthly(label: string)       { return /^\d{4}-\d{2}$/.test(label); }

function filterPeriods(labels: string[], viewMode: "quarterly" | "annual" | "monthly" | "all"): string[] {
  if (viewMode === "all")       return labels;
  if (viewMode === "quarterly") return labels.filter(isSingleQuarter);
  if (viewMode === "annual")    return labels.filter(isAnnual);
  if (viewMode === "monthly")   return labels.filter(isMonthly);
  return labels;
}

function sortPeriodLabels(labels: string[]): string[] {
  return [...labels].sort((a, b) => periodSortKey(a).localeCompare(periodSortKey(b)));
}

/**
 * Explicit indicator display order within each category.
 * Indicators not listed get priority 99 (appear last, sorted alphabetically).
 * Related indicators (余额↔增速, absolute↔delta) are placed adjacent.
 */
const INDICATOR_ORDER: Record<string, number> = {
  // ── 货币总量 ──
  "M2余额": 1,
  "M2同比增速": 2,
  "M1余额": 3,
  "M1同比增速": 4,
  // ── 信贷投放 ──
  "人民币贷款余额": 1,
  "人民币贷款余额同比增速": 2,
  "普惠小微贷款同比增速": 3,
  "制造业中长期贷款同比增速": 4,
  // ── 社会融资 ──
  "社融存量": 1,
  "社融存量同比增速": 2,
  "企业债券余额": 3,
  "政府债券余额": 4,
  "委托贷款余额": 5,
  "信托贷款余额": 6,
  "未贴现银行承兑汇票余额": 7,
  "非金融企业股票余额": 8,
  // ── 资金价格 ──
  "1年期LPR": 1,
  "5年期以上LPR": 2,
  "企业贷款加权平均利率": 3,
  "个人住房贷款加权平均利率": 4,
  // ── 信贷需求 ──
  "贷款总体需求指数": 1,
  "小微企业贷款需求指数": 2,
  "银行家宏观经济热度指数": 3,
  "货币政策感受指数": 4,
  // ── 实体景气 ──
  "经营景气指数": 1,
  "盈利指数": 2,
  "国内订单指数": 3,
  "资金周转指数": 4,
  // ── 居民意愿 ──
  "消费意愿指数": 1,
  "储蓄意愿指数": 2,
  "就业感受指数": 3,
};

function indicatorSortKey(name: string): number {
  return INDICATOR_ORDER[name] ?? 99;
}

function groupIndicators(indicators: Record<string, SeriesIndicator>): GroupedIndicators {
  const groups: GroupedIndicators = {};
  for (const [name, ind] of Object.entries(indicators)) {
    const cat = ind.category || "其他";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(name);
  }
  // Sort indicators within each category by explicit order
  for (const cat of Object.keys(groups)) {
    groups[cat].sort((a, b) => indicatorSortKey(a) - indicatorSortKey(b));
  }
  return groups;
}

// ── Pair detection ────────────────────────────────────────────────────────────

/**
 * Scale terms: these indicate the "body" (left-axis bar/line).
 * Rate terms: these go on the right axis as a dashed line.
 */
const SCALE_SUFFIXES = ["余额", "存量", "增量", "规模"] as const;
const RATE_SUFFIXES  = ["同比增速", "同比增长", "增速", "增长率", "同比"] as const;

/** Explicit fixed pairs that share the same semantic subject */
const EXPLICIT_PAIRS: [string, string][] = [
  ["人民币贷款余额",     "人民币贷款同比增速"],
  ["M2余额",            "M2同比增速"],
  ["M1余额",            "M1同比增速"],
  ["社融存量",          "社融存量同比增速"],
  ["社会融资规模存量",   "社融存量同比增速"],
  ["新增人民币贷款",     "人民币贷款同比增速"],
  ["普惠小微贷款余额",  "普惠小微贷款同比增速"],
  ["制造业中长期贷款余额", "制造业中长期贷款同比增速"],
  ["小额贷款公司贷款余额", "小额贷款公司贷款余额同比增速"],
  // 社融存量月报分项
  ["企业债券余额",       "企业债券余额同比增速"],
  ["政府债券余额",       "政府债券余额同比增速"],
  ["委托贷款余额",       "委托贷款余额同比增速"],
  ["信托贷款余额",       "信托贷款余额同比增速"],
  ["未贴现银行承兑汇票余额", "未贴现银行承兑汇票余额同比增速"],
  ["非金融企业股票余额", "非金融企业股票余额同比增速"],
];

/**
 * 口径调整标注：当指标名匹配时，在图表中对应期次画参考线并显示说明。
 * period 用 displayPeriod 格式（如 "24M12"）。
 */
const CALIBER_ADJUSTMENTS: {
  match: (name: string) => boolean;
  period: string;        // 对应 data[].displayPeriod 里的值
  rawPeriod: string;     // 原始 period label 用于精确匹配
  label: string;
  note: string;
}[] = [
  {
    match: (name) => name.includes("M1"),
    period: "24M12",
    rawPeriod: "2024-12",
    label: "口径调整",
    note: "2024.12起 M1 纳入个人活期存款，历史已回溯修订",
  },
];

/**
 * 口径切换期异常值过滤：在指定期间内，同比增速超过阈值的视为口径噪声，置为 null。
 */
const CALIBER_NOISE_FILTERS: {
  match: (primaryName: string, secondaryName: string) => boolean;
  periodRange: [string, string];  // [startPeriod, endPeriod] inclusive (raw period label)
  field: "primary" | "secondary";
  threshold: number;              // abs value > threshold → null
}[] = [
  {
    match: (_p, s) => s?.includes("M1") && s?.includes("增速") || false,
    periodRange: ["2024-01", "2024-11"],
    field: "secondary",
    threshold: 30,
  },
];

/**
 * Given any metric name, return its paired companion (scale↔rate), or null.
 * The relationship is symmetric: findCompanion(findCompanion(x)) === x.
 */
function findCompanion(
  metricName: string,
  allIndicators: Record<string, SeriesIndicator>
): string | null {
  const names = Object.keys(allIndicators);

  // 1. Check explicit pairs first (both directions)
  for (const [a, b] of EXPLICIT_PAIRS) {
    if (metricName === a && names.includes(b)) return b;
    if (metricName === b && names.includes(a)) return a;
  }

  // 2. Pattern: strip known suffix → find candidate with complementary suffix
  for (const scaleSfx of SCALE_SUFFIXES) {
    if (metricName.endsWith(scaleSfx)) {
      const stem = metricName.slice(0, -scaleSfx.length);
      for (const rateSfx of RATE_SUFFIXES) {
        const candidate = stem + rateSfx;
        if (names.includes(candidate)) return candidate;
      }
      // Also try: stem alone ends in a noun, rate suffix appended differently
      const fuzzy = names.find(
        (n) => n !== metricName && n.startsWith(stem) &&
               RATE_SUFFIXES.some((sfx) => n.endsWith(sfx))
      );
      if (fuzzy) return fuzzy;
    }
  }
  for (const rateSfx of RATE_SUFFIXES) {
    if (metricName.endsWith(rateSfx)) {
      const stem = metricName.slice(0, -rateSfx.length);
      for (const scaleSfx of SCALE_SUFFIXES) {
        const candidate = stem + scaleSfx;
        if (names.includes(candidate)) return candidate;
      }
      const fuzzy = names.find(
        (n) => n !== metricName && n.startsWith(stem) &&
               SCALE_SUFFIXES.some((sfx) => n.endsWith(sfx))
      );
      if (fuzzy) return fuzzy;
    }
  }

  return null;
}

/**
 * Build a deduplicated list of "primary" metrics for the left panel.
 * When two indicators are companions, only the scale one (余额/增量/存量) is shown;
 * clicking it shows both on the dual-axis chart.
 * Rate-only metrics (no companion) are still shown individually.
 */
function buildPrimaryList(
  names: string[],
  allIndicators: Record<string, SeriesIndicator>
): string[] {
  const suppressed = new Set<string>();
  for (const name of names) {
    const companion = findCompanion(name, allIndicators);
    if (companion) {
      // Suppress the rate metric — it's shown via the scale metric's chart
      const isRate = RATE_SUFFIXES.some((sfx) => name.endsWith(sfx));
      if (isRate) suppressed.add(name);
    }
  }
  return names.filter((n) => !suppressed.has(n));
}

// ── MetricTree ────────────────────────────────────────────────────────────────

const CATEGORY_ORDER = ["货币供应量", "社会融资", "贷款", "存款", "利率", "宏观经济", "景气指数", "汇率", "流动性", "其他"];

function MetricTree({
  indicators,
  activeMetric,
  onSelect,
}: {
  indicators: Record<string, SeriesIndicator>;
  activeMetric: string | null;
  onSelect: (name: string) => void;
}) {
  // Deduplicated names: rate metrics with a companion are suppressed
  const displayNames = useMemo(
    () => buildPrimaryList(Object.keys(indicators), indicators),
    [indicators]
  );
  const groups = useMemo(() => groupIndicators(
    // re-group using displayNames only
    displayNames.reduce<Record<string, SeriesIndicator>>((acc, n) => {
      acc[n] = indicators[n]; return acc;
    }, {})
  ), [displayNames, indicators]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ "其他": true });
  const toggleGroup = (cat: string) =>
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const sortedCats = Object.keys(groups).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return (
    <div className="text-sm">
      {sortedCats.map((cat) => {
        const allNames = groups[cat];
        const isCollapsed = collapsed[cat] ?? false;

        return (
          <div key={cat} className="mb-1">
            {/* Sticky category header */}
            <button
              onClick={() => toggleGroup(cat)}
              className="w-full flex items-center justify-between px-2 py-1 text-[10px] font-semibold text-ink-faint uppercase tracking-wider hover:text-ink-secondary rounded hover:bg-surface sticky top-0 bg-surface-raised dark:bg-surface-raised z-10"
            >
              <span>{cat}</span>
              <span className="text-ink-faint/60 text-[10px]">
                {isCollapsed ? "▸" : "▾"}
              </span>
            </button>

            {!isCollapsed && (
              <div className="mt-0.5 space-y-0.5">
                {allNames.map((name) => {
                  const companion = findCompanion(name, indicators);
                  const isActive  = activeMetric === name ||
                    (companion !== null && activeMetric === companion);
                  const periodCount = Object.keys(indicators[name].values).length;
                  const isSparse = periodCount < 3;

                  return (
                    <button
                      key={name}
                      onClick={() => onSelect(name)}
                      className={`w-full text-left px-2 py-1.5 rounded transition-colors flex items-center gap-1 ${
                        isActive
                          ? "bg-blue-600 text-white"
                          : isSparse
                          ? "text-ink-faint hover:bg-surface hover:text-ink-secondary opacity-50"
                          : "text-ink-secondary hover:bg-surface hover:text-ink"
                      }`}
                      title={
                        companion
                          ? `${name} + ${companion}（双轴图，左轴总量 / 右轴增速）${indicators[name].note ? `\n口径：${indicators[name].note}` : ""}`
                          : isSparse
                          ? `${name}（仅 ${periodCount} 期，数据不足）`
                          : indicators[name].note
                          ? `${name}\n口径：${indicators[name].note}`
                          : name
                      }
                    >
                      <span className={`truncate flex-1 ${isSparse ? "text-xs" : "text-xs"}`}>
                        {name}
                      </span>
                      {companion && (
                        <span
                          className={`flex-shrink-0 text-[10px] font-medium ${
                            isActive ? "text-blue-200" : "text-ink-faint"
                          }`}
                          title="包含同比增速（右轴）"
                        >
                          ↕
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── ComboChart: dual-Y-axis composed chart ─────────────────────────────────────

const COLOR_PRIMARY   = "#3b82f6";  // blue  — bar / primary line
const COLOR_SECONDARY = "#f59e0b";  // amber — secondary line (growth rate)

function ComboChart({
  primaryName,
  primaryIndicator,
  secondaryName,
  secondaryIndicator,
  periodMode,
}: {
  primaryName: string;
  primaryIndicator: SeriesIndicator;
  secondaryName: string | null;
  secondaryIndicator: SeriesIndicator | null;
  periodMode: PeriodMode;
}) {
  // Build sorted period union
  const allPeriodsSet = new Set([
    ...filterPeriods(Object.keys(primaryIndicator.values), periodMode),
    ...(secondaryIndicator
      ? filterPeriods(Object.keys(secondaryIndicator.values), periodMode)
      : []),
  ]);
  const periods = sortPeriodLabels([...allPeriodsSet]);

  const data = periods.map((period) => {
    return {
      period,
      displayPeriod: shortPeriodLabel(period),
      primary: primaryIndicator.values[period]?.num ?? null,
      primaryRaw: primaryIndicator.values[period]?.raw ?? "",
      primaryDerived: (primaryIndicator.values[period] as { derived?: boolean })?.derived ?? false,
      primarySpans: (primaryIndicator.values[period] as { spans?: number })?.spans ?? null,
      primaryArticleId: (primaryIndicator.values[period] as { article_id?: number })?.article_id ?? null,
      secondary: secondaryIndicator?.values[period]?.num ?? null,
      secondaryRaw: secondaryIndicator?.values[period]?.raw ?? "",
    };
  });

  // 口径切换期异常值过滤
  const sName = secondaryName ?? "";
  for (const nf of CALIBER_NOISE_FILTERS) {
    if (!nf.match(primaryName, sName)) continue;
    for (const d of data) {
      if (d.period >= nf.periodRange[0] && d.period <= nf.periodRange[1]) {
        const val = d[nf.field] as number | null;
        if (val !== null && Math.abs(val) > nf.threshold) {
          (d as any)[nf.field] = null;
        }
      }
    }
  }

  if (data.length === 0) {
    return <p className="text-xs text-ink-faint py-8 text-center">该视角暂无数据</p>;
  }

  // Guard: if fewer than 2 periods have actual values for the primary series, skip chart
  const primaryDataPoints = data.filter((d) => d.primary !== null).length;
  if (primaryDataPoints < 2) {
    return <p className="text-xs text-ink-faint py-8 text-center">该视角数据不足（仅 {primaryDataPoints} 期），请切换时间粒度</p>;
  }

  const primaryUnit    = primaryIndicator.unit   ? `(${primaryIndicator.unit})` : "";
  const secondaryUnit  = secondaryIndicator?.unit ? `(${secondaryIndicator.unit})` : "";
  const hasDual        = secondaryIndicator !== null;
  const isYtd          = primaryIndicator.ytd === true;
  // YTD cumulative indicators should be line charts (they're monotonically increasing
  // within a year then reset — a bar chart would falsely imply single-period values).
  // Also avoid bar for paired dual-axis if ytd.
  const useBar = !isYtd && (hasDual || primaryIndicator.chart_type === "bar");

  // Collect source article_ids for footnote
  const articleIds = Array.from(
    new Set(
      data
        .map((d) => d.primaryArticleId)
        .filter((id): id is number => id !== null)
    )
  ).slice(0, 3);

  // Custom tooltip
  const CustomTooltip = ({
    active, payload, label,
  }: { active?: boolean; payload?: Array<{ payload: { period: string; primaryRaw: string; secondaryRaw: string; primaryDerived: boolean; primarySpans: number | null } }>; label?: string }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="rounded-lg border-subtle bg-surface-raised shadow-lg p-2.5 text-xs max-w-[220px]">
        <p className="font-semibold text-ink-secondary mb-1.5 border-b border-border pb-1">{d.period || label}</p>
        {d.primaryRaw && (
          <p className="text-blue-600 mb-0.5">
            {primaryName}: <span className="font-medium">{d.primaryRaw}</span>
            {d.primaryDerived && <span className="ml-1 text-ink-faint">(推算)</span>}
            {d.primarySpans && d.primarySpans > 1 && (
              <span className="ml-1 text-orange-400">({d.primarySpans}月合计)</span>
            )}
          </p>
        )}
        {d.secondaryRaw && secondaryName && (
          <p className="text-amber-600">
            {secondaryName}: <span className="font-medium">{d.secondaryRaw}</span>
          </p>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Legend-like subtitle */}
      <div className="flex items-center gap-4 text-xs text-ink-tertiary mb-2 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-blue-500 opacity-70" />
          <span>{primaryName} {primaryUnit}</span>
        </span>
        {hasDual && secondaryName && (
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block w-5 border-t-2 border-dashed border-amber-400"
              style={{ verticalAlign: "middle" }}
            />
            <span>{secondaryName} {secondaryUnit}</span>
          </span>
        )}
        {isYtd && (
          <span className="text-[10px] bg-orange-50 text-orange-500 border border-orange-200 px-1.5 py-0.5 rounded">
            年累计
          </span>
        )}
        {data.some((d) => d.primarySpans && d.primarySpans > 1) && (
          <span className="text-[10px] text-ink-faint">
            * 部分柱子覆盖多月（原始报告未逐月发布）
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ left: 0, right: hasDual ? 44 : 16, top: 8, bottom: 44 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="displayPeriod"
            tick={{ fontSize: 9 }}
            angle={-30}
            textAnchor="end"
            interval={data.length > 24 ? Math.ceil(data.length / 24) - 1 : 0}
            height={44}
          />
          {/* Left Y-axis: primary (bar or line) */}
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 10 }}
            width={55}
            tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v))}
          />
          {/* Right Y-axis: secondary (growth rate line) */}
          {hasDual && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10 }}
              width={40}
              tickFormatter={(v) => `${v}%`}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          {hasDual && (
            <Legend
              wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
              formatter={(value) =>
                value === "primary" ? primaryName : secondaryName ?? value
              }
            />
          )}

          {/* Primary series */}
          {useBar ? (
            <Bar
              yAxisId="left"
              dataKey="primary"
              name="primary"
              fill={COLOR_PRIMARY}
              fillOpacity={0.75}
              radius={[3, 3, 0, 0]}
            />
          ) : (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="primary"
              name="primary"
              stroke={COLOR_PRIMARY}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          )}

          {/* Secondary series (growth rate on right axis) */}
          {hasDual && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="secondary"
              name="secondary"
              stroke={COLOR_SECONDARY}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          )}

          {/* 口径调整标注线 */}
          {CALIBER_ADJUSTMENTS
            .filter((adj) => adj.match(primaryName))
            .map((adj) => {
              // 找到 data 中匹配的 displayPeriod index
              const idx = data.findIndex((d: any) => d.displayPeriod === adj.period || d.period === adj.rawPeriod);
              if (idx < 0) return null;
              const xVal = data[idx]?.displayPeriod;
              if (!xVal) return null;
              return (
                <ReferenceLine
                  key={adj.rawPeriod}
                  x={xVal}
                  yAxisId="left"
                  stroke="#dc2626"
                  strokeDasharray="4 2"
                  strokeWidth={1.5}
                  label={{
                    value: "⬆ 口径调整",
                    position: "insideTopLeft",
                    fontSize: 9,
                    fill: "#dc2626",
                    fontWeight: 600,
                    offset: 5,
                  }}
                />
              );
            })}
        </ComposedChart>
      </ResponsiveContainer>

      {/* 口径调整说明 */}
      {CALIBER_ADJUSTMENTS
        .filter((adj) => adj.match(primaryName))
        .map((adj) => (
          <p key={adj.rawPeriod} className="text-[10px] text-rose-700 mt-1.5 pl-1 border-l-2 border-rose-300">
            {adj.note}
          </p>
        ))}

      {/* Source footnote */}
      {articleIds.length > 0 && (
        <p className="text-[10px] text-ink-faint mt-1 text-right">
          数据来源：
          {articleIds.map((id, i) => (
            <span key={id}>
              {i > 0 && "、"}
              <a
                href={`/article/${id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-ink-secondary transition-colors"
              >
                文章 #{id}
              </a>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

// ── MetricsTable ──────────────────────────────────────────────────────────────

function MetricsTable({
  selectedMetric,
  companionMetric,
  indicators,
  periodMode,
}: {
  selectedMetric: string;
  companionMetric: string | null;
  indicators: Record<string, SeriesIndicator>;
  periodMode: PeriodMode;
}) {
  const primaryInd = indicators[selectedMetric];
  if (!primaryInd) return null;

  const companionInd = companionMetric ? indicators[companionMetric] ?? null : null;

  // Determine delta column semantics:
  // - For growth-rate or index metrics, period-over-period diff = "同比变化 △"
  // - For absolute-value metrics, it's "环比 △"
  const isRateMetric = RATE_SUFFIXES.some((sfx) => selectedMetric.endsWith(sfx)) ||
    ["景气指数", "满意度", "信心指数", "扩散指数", "净占比"].some((k) => selectedMetric.includes(k));
  const deltaLabel = isRateMetric ? "同比变化 △" : "环比 △";

  // Build period list from selected metric (+ companion)
  const rawPeriods = Array.from(
    new Set([
      ...filterPeriods(Object.keys(primaryInd.values), periodMode),
      ...(companionInd ? filterPeriods(Object.keys(companionInd.values), periodMode) : []),
    ])
  );
  const allPeriods = sortPeriodLabels(rawPeriods);

  if (allPeriods.length === 0) {
    return <p className="text-xs text-ink-faint py-8 text-center">该视角暂无数据</p>;
  }

  // Most recent first
  const displayPeriods = [...allPeriods].reverse();

  const showCompanion = companionInd !== null && companionMetric !== null;

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="bg-surface-sunken border-b border-border">
            <th className="sticky left-0 bg-surface-sunken px-3 py-2.5 text-left font-medium text-ink-tertiary whitespace-nowrap">
              期次
            </th>
            <th className="px-3 py-2.5 text-right font-medium text-ink-secondary whitespace-nowrap">
              {selectedMetric}
              {primaryInd.unit && (
                <span className="ml-1 text-ink-faint font-normal">({primaryInd.unit})</span>
              )}
              {primaryInd.ytd && (
                <span className="ml-1 text-[10px] text-orange-500 font-normal">年累计</span>
              )}
            </th>
            <th className="px-3 py-2.5 text-right font-medium text-ink-faint whitespace-nowrap w-20">
              {deltaLabel}
            </th>
            {showCompanion && (
              <th className="px-3 py-2.5 text-right font-medium text-amber-600 whitespace-nowrap">
                {companionMetric}
                {companionInd!.unit && (
                  <span className="ml-1 text-amber-400 font-normal">({companionInd!.unit})</span>
                )}
              </th>
            )}
            {showCompanion && (
              <th className="px-3 py-2.5 text-right font-medium text-ink-faint whitespace-nowrap w-20">
                {deltaLabel}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {displayPeriods.map((period, rowIdx) => {
            const prevPeriod = displayPeriods[rowIdx + 1];

            const entry = primaryInd.values[period] as { raw?: string; num?: number | null; derived?: boolean } | undefined;
            const raw = entry?.raw ?? "";
            const num = entry?.num ?? null;
            const isDerived = entry?.derived ?? false;
            const prevNum = prevPeriod
              ? ((primaryInd.values[prevPeriod] as { num?: number | null })?.num ?? null)
              : null;

            let delta: number | null = null;
            let deltaColor = "text-ink-faint";
            if (num !== null && prevNum !== null) {
              delta = num - prevNum;
              deltaColor = delta > 0 ? "text-green-600" : delta < 0 ? "text-red-500" : "text-ink-faint";
            }

            const cEntry = companionInd ? (companionInd.values[period] as { raw?: string; num?: number | null } | undefined) : undefined;
            const cRaw = cEntry?.raw ?? "";
            const cNum = cEntry?.num ?? null;
            const cPrevNum = prevPeriod && companionInd
              ? ((companionInd.values[prevPeriod] as { num?: number | null })?.num ?? null)
              : null;
            let cDelta: number | null = null;
            let cDeltaColor = "text-ink-faint";
            if (cNum !== null && cPrevNum !== null) {
              cDelta = cNum - cPrevNum;
              cDeltaColor = cDelta > 0 ? "text-green-600" : cDelta < 0 ? "text-red-500" : "text-ink-faint";
            }

            return (
              <tr key={period} className="border-b border-border/50 hover:bg-surface transition-colors">
                <td className="sticky left-0 bg-surface-raised px-3 py-2 font-medium text-ink-secondary whitespace-nowrap">
                  {period}
                </td>
                <td
                  className={`px-3 py-2 text-right whitespace-nowrap ${
                    raw ? "text-ink" : "text-ink-faint/50"
                  } ${isDerived ? "italic" : ""}`}
                  title={isDerived ? "推算值（由累计值反算）" : undefined}
                >
                  {raw || "—"}
                </td>
                <td className={`px-3 py-2 text-right whitespace-nowrap tabular-nums ${deltaColor}`}>
                  {delta !== null
                    ? `${delta > 0 ? "+" : ""}${delta.toFixed(
                        Math.abs(delta) < 1 ? 2 : 1
                      )}`
                    : "—"}
                </td>
                {showCompanion && (
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${cRaw ? "text-amber-700" : "text-ink-faint/50"}`}>
                    {cRaw || "—"}
                  </td>
                )}
                {showCompanion && (
                  <td className={`px-3 py-2 text-right whitespace-nowrap tabular-nums ${cDeltaColor}`}>
                    {cDelta !== null
                      ? `${cDelta > 0 ? "+" : ""}${cDelta.toFixed(
                          Math.abs(cDelta) < 1 ? 2 : 1
                        )}`
                      : "—"}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── SeriesDataPanel (main) ────────────────────────────────────────────────────

const SERIES_TABS = [
  "货币政策执行报告",
  "金融统计月报",
  "社融增量月报",
  "社融存量月报",
  "小额贷款公司季报",
  "银行家问卷调查",
  "企业家问卷调查",
  "城镇储户问卷调查",
];

const TOPIC_ORDER = [
  "供给·货币总量",
  "供给·信贷投放",
  "供给·社会融资",
  "供给·资金价格",
  "需求·信贷需求",
  "需求·实体景气",
  "需求·居民意愿",
];

/** Split "供给·货币总量" → { group: "供给", label: "货币总量" } */
function parseTopic(topic: string) {
  const idx = topic.indexOf("·");
  if (idx === -1) return { group: "", label: topic };
  return { group: topic.slice(0, idx), label: topic.slice(idx + 1) };
}

const PERIOD_MODE_LABEL: Record<PeriodMode, string> = {
  quarterly: "季",
  annual: "年",
  monthly: "月",
};

function availablePeriodModes(indicators: Record<string, SeriesIndicator>): PeriodMode[] {
  const allPeriods = new Set(
    Object.values(indicators).flatMap((ind) => Object.keys(ind.values))
  );
  const modes: PeriodMode[] = [];
  if ([...allPeriods].some(isSingleQuarter)) modes.push("quarterly");
  if ([...allPeriods].some(isAnnual))        modes.push("annual");
  if ([...allPeriods].some(isMonthly))       modes.push("monthly");
  return modes;
}

/** Filter period modes to only those that have data for the given indicator (+ its companion) */
function availableModesForIndicator(
  metricName: string | null,
  companionName: string | null,
  indicators: Record<string, SeriesIndicator>
): PeriodMode[] {
  if (!metricName || !indicators[metricName]) return availablePeriodModes(indicators);

  const periods = new Set([
    ...Object.keys(indicators[metricName].values),
    ...(companionName && indicators[companionName]
      ? Object.keys(indicators[companionName].values)
      : []),
  ]);

  const modes: PeriodMode[] = [];
  if ([...periods].some(isSingleQuarter)) modes.push("quarterly");
  if ([...periods].some(isAnnual))        modes.push("annual");
  if ([...periods].some(isMonthly))       modes.push("monthly");
  return modes;
}

export default function SeriesDataPanel() {
  const [mode, setMode] = useState<"topic" | "series">("topic");
  const [activeTopic, setActiveTopic] = useState<string>(TOPIC_ORDER[0]);
  const [activeSeries, setActiveSeries] = useState(SERIES_TABS[0]);
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");
  const [periodMode, setPeriodMode] = useState<PeriodMode>("monthly");

  // Topic mode data
  const { data: topicData = [], isLoading: topicLoading, isError: topicError } = useQuery({
    queryKey: ["topic-metrics"],
    queryFn: () => fetchTopicMetrics(),
    staleTime: 10 * 60 * 1000,  // 10min — 数据不常变
    enabled: mode === "topic",
  });

  // Series mode data
  const { data: seriesData = [], isLoading: seriesLoading, isError: seriesError } = useQuery({
    queryKey: ["series-metrics", activeSeries],
    queryFn: () => fetchSeriesMetrics(activeSeries),
    staleTime: 10 * 60 * 1000,
    enabled: mode === "series",
  });

  const isLoading = mode === "topic" ? topicLoading : seriesLoading;
  const isError = mode === "topic" ? topicError : seriesError;

  // Available topics from API (ordered)
  const availableTopics = useMemo(() => {
    const topicKeys = (topicData as TopicMetricsData[]).map((t) => t.topic);
    // Sort by predefined order, then append any extras
    const ordered = TOPIC_ORDER.filter((t) => topicKeys.includes(t));
    const extras = topicKeys.filter((t) => !TOPIC_ORDER.includes(t));
    return [...ordered, ...extras];
  }, [topicData]);

  // Group topics by supply/demand for optgroup
  const topicGroups = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const t of availableTopics) {
      const { group } = parseTopic(t);
      const key = group || "其他";
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    }
    return groups;
  }, [availableTopics]);

  // Resolve current indicators based on mode
  const currentIndicators: Record<string, SeriesIndicator> = useMemo(() => {
    if (mode === "topic") {
      const topicEntry = (topicData as TopicMetricsData[]).find((t) => t.topic === activeTopic);
      return topicEntry?.indicators ?? {};
    } else {
      const seriesEntry = (seriesData as SeriesMetricsData[]).find((d) => d.series_name === activeSeries);
      return seriesEntry?.indicators ?? {};
    }
  }, [mode, topicData, seriesData, activeTopic, activeSeries]);

  const indicators = currentIndicators;
  const indicatorNames = Object.keys(indicators);

  // Deduplicated display list (rate metrics with companions are suppressed)
  const displayNames = useMemo(
    () => buildPrimaryList(indicatorNames, indicators),
    [indicatorNames, indicators]
  );

  // Auto-select: prefer first display metric with >= 3 periods
  const firstMultiPeriod = useMemo(
    () =>
      displayNames.find((n) => Object.keys(indicators[n].values).length >= 3) ??
      displayNames[0] ??
      null,
    [displayNames, indicators]
  );

  // Normalise activeMetric: if user somehow selected a rate metric, remap to its scale companion
  const normalisedActive = useMemo(() => {
    if (!activeMetric || !indicators[activeMetric]) return null;
    const companion = findCompanion(activeMetric, indicators);
    const isRate = RATE_SUFFIXES.some((sfx) => activeMetric.endsWith(sfx));
    if (isRate && companion && indicators[companion]) return companion;
    return activeMetric;
  }, [activeMetric, indicators]);

  const effectiveMetric = normalisedActive ?? firstMultiPeriod;

  // Companion for dual-axis
  const companionMetric = useMemo(
    () => (effectiveMetric ? findCompanion(effectiveMetric, indicators) : null),
    [effectiveMetric, indicators]
  );

  // Only show period modes that have data for the current PRIMARY indicator.
  // Companion is excluded — it's an auxiliary axis and should not inflate the
  // set of selectable time granularities (e.g. a quarterly-only rate metric
  // should not gain a "monthly" toggle just because its companion LPR is monthly).
  const periodModes = useMemo(
    () => availableModesForIndicator(effectiveMetric, null, indicators),
    [effectiveMetric, indicators]
  );

  // Auto-fallback: even if a mode is "available" (has ≥1 matching period),
  // the chart needs ≥2 data points.  Prefer the user's selected mode, but
  // if it yields <2 points for the current indicator, fall back to the first
  // mode that does.
  const effectivePeriodMode: PeriodMode = useMemo(() => {
    const countPoints = (mode: PeriodMode) => {
      if (!effectiveMetric || !indicators[effectiveMetric]) return 0;
      return filterPeriods(Object.keys(indicators[effectiveMetric].values), mode).length;
    };
    if (periodModes.includes(periodMode) && countPoints(periodMode) >= 2) {
      return periodMode;
    }
    // Fall back to the first mode with sufficient data
    const fallback = periodModes.find((m) => countPoints(m) >= 2);
    return fallback ?? periodModes[0] ?? "quarterly";
  }, [periodMode, periodModes, effectiveMetric, indicators]);

  const handleSeriesChange = (series: string) => {
    setActiveSeries(series);
    setActiveMetric(null);
  };

  const handleTopicChange = (topic: string) => {
    setActiveTopic(topic);
    setActiveMetric(null);
  };

  const handleModeToggle = (m: "topic" | "series") => {
    setMode(m);
    setActiveMetric(null);
  };

  return (
    <div className="paper-panel overflow-hidden">
      {/* Header row: mode toggle + selector + view controls */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {/* Mode toggle: 主题 / 数据源 */}
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            <button
              onClick={() => handleModeToggle("topic")}
              className={`px-2.5 py-1.5 transition-colors ${
                mode === "topic" ? "bg-ink text-surface font-medium dark:bg-ink dark:text-surface" : "text-ink-tertiary hover:bg-surface"
              }`}
            >
              主题
            </button>
            <button
              onClick={() => handleModeToggle("series")}
              className={`px-2.5 py-1.5 transition-colors ${
                mode === "series" ? "bg-ink text-surface font-medium dark:bg-ink dark:text-surface" : "text-ink-tertiary hover:bg-surface"
              }`}
            >
              数据源
            </button>
          </div>

          {mode === "topic" ? (
            <select
              value={activeTopic}
              onChange={(e) => handleTopicChange(e.target.value)}
              className="text-xs font-medium text-ink-secondary border border-border rounded-md px-2.5 py-1.5 bg-surface-raised focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer max-w-[180px]"
            >
              {Object.entries(topicGroups).map(([group, topics]) => (
                <optgroup key={group} label={group}>
                  {topics.map((t) => (
                    <option key={t} value={t}>{parseTopic(t).label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          ) : (
            <select
              value={activeSeries}
              onChange={(e) => handleSeriesChange(e.target.value)}
              className="text-xs font-medium text-ink-secondary border border-border rounded-md px-2.5 py-1.5 bg-surface-raised focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer max-w-[220px]"
            >
              {SERIES_TABS.map((tab) => (
                <option key={tab} value={tab}>{tab}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Period mode — only show buttons for modes with data */}
          {periodModes.length > 1 && (
            <div className="flex rounded-md border border-border overflow-hidden text-xs">
              {periodModes.map((m) => (
                <button
                  key={m}
                  onClick={() => setPeriodMode(m)}
                  className={`px-2.5 py-1.5 transition-colors ${
                    effectivePeriodMode === m
                      ? "bg-ink text-surface font-medium dark:bg-ink dark:text-surface"
                      : "text-ink-tertiary hover:bg-surface"
                  }`}
                >
                  {PERIOD_MODE_LABEL[m]}
                </button>
              ))}
            </div>
          )}

          {/* Chart / Table toggle */}
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            {(["chart", "table"] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 transition-colors ${
                  viewMode === mode
                    ? "bg-ink text-surface font-medium dark:bg-ink dark:text-surface"
                    : "text-ink-tertiary hover:bg-surface"
                }`}
              >
                {mode === "chart" ? "图表" : "表格"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading / Error / Empty states */}
      {isLoading && (
        <div className="p-10 text-center text-xs text-ink-faint">加载中...</div>
      )}
      {isError && (
        <div className="p-10 text-center text-xs text-negative">数据加载失败，请稍后重试</div>
      )}
      {!isLoading && !isError && indicatorNames.length === 0 && (
        <div className="p-12 text-center">
          <div className="text-4xl mb-3 text-ink-faint/40">📊</div>
          <p className="text-sm text-ink-tertiary font-medium">该系列暂无历史指标数据</p>
          <p className="text-xs text-ink-faint mt-1">数据将在分析完成后自动更新</p>
        </div>
      )}

      {/* Main content: two-column layout */}
      {!isLoading && !isError && indicatorNames.length > 0 && (
        <div className="flex flex-col md:flex-row" style={{ minHeight: 380 }}>
          {/* Left panel: metric tree */}
          <div
            className="border-r-0 md:border-r md:border-border p-3 flex-shrink-0"
            style={{ width: undefined }}
          >
            <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-widest mb-2 px-1">
              指标
            </p>
            <MetricTree
              indicators={indicators}
              activeMetric={effectiveMetric}
              onSelect={(name) => {
                setActiveMetric(name);
                setViewMode("chart");
              }}
            />
          </div>

          {/* Right panel: chart/table area (flex-grow) */}
          <div className="flex-1 p-4 min-w-0">
            {/* Selected metric title */}
            <div className="mb-3">
              <p className="text-sm font-semibold text-ink truncate leading-snug">
                {effectiveMetric ?? "—"}
              </p>
              {effectiveMetric && indicators[effectiveMetric]?.note && (
                <p className="text-[11px] text-ink-faint mt-0.5 leading-relaxed">
                  {indicators[effectiveMetric].note}
                </p>
              )}
              {companionMetric && viewMode === "chart" && (
                <p className="text-xs text-amber-500 mt-0.5">
                  含同比增速：{companionMetric}
                </p>
              )}
              {effectiveMetric && indicators[effectiveMetric]?.ytd && (
                <p className="text-[11px] text-orange-500 mt-0.5">
                  注：为年初至当月累计值（非当月单月增量）
                </p>
              )}
            </div>

            {viewMode === "chart" && effectiveMetric && indicators[effectiveMetric] && (
              <ComboChart
                primaryName={effectiveMetric}
                primaryIndicator={indicators[effectiveMetric]}
                secondaryName={companionMetric}
                secondaryIndicator={companionMetric ? indicators[companionMetric] ?? null : null}
                periodMode={effectivePeriodMode}
              />
            )}

            {viewMode === "table" && effectiveMetric && (
              <MetricsTable
                selectedMetric={effectiveMetric}
                companionMetric={companionMetric}
                indicators={indicators}
                periodMode={effectivePeriodMode}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
